import {
    clientAccountFieldError,
    isBlankValue,
    isDestinationField,
    isSecretName,
    type ProviderAccount,
    type ProviderFieldError,
    SECRET_COLUMN,
    SECRET_NAMES,
} from '@/domain/providerConfig';
import { toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import {
    normalizeValue,
    sameValue,
    secretChange,
} from '@/routes/shared/ProviderConfig/form/formLeaves';

import { type AccountFormPath, type AccountFormValues, accountValueFields } from './accountForm';

/**
 * The server's account rules (account-plan.ts rowErrors) over the row the form would save:
 * every value's format, path-safe share and schema, known objects, and while the account is
 * active its required values and secrets. The server stays the authority.
 */
export function accountFormErrors(
    account: ProviderAccount,
    values: AccountFormValues
): ProviderFieldError[] {
    const errors: ProviderFieldError[] = [];
    for (const field of accountValueFields(account)) {
        const path = `fields.${toFormKey(field.key)}`;
        const value = normalizeValue(values.fields[toFormKey(field.key)]);
        // As on the server, a stored value is not re-judged; only one the save would write.
        const written = !sameValue(value, account.fields[field.key]);
        const judged = written || isDestinationField(field);
        const code = judged ? clientAccountFieldError(field, value) : null;
        // As on the server, a key no save can write is not asked for, nor one the save leaves
        // holding a value the field cannot read: that value is stored, so it is set.
        const unreadable = account.unreadable.filter((entry) => entry.key === field.key);
        const blocked = unreadable.some((entry) => entry.blocked);
        const held = !written && unreadable.length > 0;
        if (code !== null) errors.push({ path, code });
        else if (values.isActive && field.required && !blocked && !held && isBlankValue(value))
            errors.push({ path, code: 'required' });
    }
    if (!values.isActive) return errors;
    for (const name of SECRET_NAMES) {
        const field = account.schemaFields.find((entry) => entry.key === SECRET_COLUMN[name]);
        if (field === undefined || field.kind !== 'secret' || !field.required) continue;
        const change = secretChange(values.secrets[name]);
        const set = change === undefined ? account.credentials[name] === true : change !== null;
        if (!set) errors.push({ path: `secrets.${name}`, code: 'required' });
    }
    return errors;
}

export function isAccountFormPath(path: string): path is AccountFormPath {
    if (path.startsWith('fields.')) return path.length > 'fields.'.length;
    return path.startsWith('secrets.') && isSecretName(path.slice('secrets.'.length));
}
